class VeloraMotion {
  constructor() {
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    this.revealObserver = null;
    this.parallaxObserver = null;
    this.parallaxItems = new Set();
    this.parallaxFrame = 0;
    this.onParallaxScroll = this.onParallaxScroll.bind(this);
    this.onReveal = this.onReveal.bind(this);
  }

  start(root = document) {
    this.prepareWords(root);
    this.prepareReveals(root);
    this.prepareParallax(root);
    document.documentElement.classList.add('motion-ready');
  }

  prepareWords(root) {
    if (
      this.reduceMotion ||
      typeof document.createTreeWalker !== 'function' ||
      typeof document.createDocumentFragment !== 'function'
    )
      return;

    root
      .querySelectorAll('[data-reveal="words"]:not([data-words-ready])')
      .forEach((element) => {
        if (element.closest('[data-section-motion="none"]')) {
          element.dataset.wordsReady = 'true';
          return;
        }
        const accessibleText = element.textContent.replace(/\s+/g, ' ').trim();
        if (!accessibleText) return;

        element.dataset.wordsReady = 'true';
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode);

        let wordIndex = 0;
        textNodes.forEach((textNode) => {
          if (!textNode.nodeValue.trim()) return;
          const fragment = document.createDocumentFragment();
          textNode.nodeValue.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              fragment.appendChild(document.createTextNode(part));
              return;
            }
            const shell = document.createElement('span');
            shell.className = 'reveal-word-shell';
            shell.setAttribute('aria-hidden', 'true');
            const word = document.createElement('span');
            word.className = 'reveal-word';
            word.style.setProperty('--word-index', wordIndex);
            word.textContent = part;
            shell.appendChild(word);
            fragment.appendChild(shell);
            wordIndex += 1;
          });
          textNode.replaceWith(fragment);
        });

        const accessibleCopy = document.createElement('span');
        accessibleCopy.className = 'visually-hidden';
        accessibleCopy.textContent = accessibleText;
        element.prepend(accessibleCopy);
      });
  }

  prepareReveals(root) {
    const elements = [
      ...root.querySelectorAll('[data-reveal]:not([data-reveal-ready])'),
    ];
    if (!elements.length) return;

    const enabledElements = [];
    elements.forEach((element) => {
      element.dataset.revealReady = 'true';
      if (element.closest('[data-section-motion="none"]'))
        element.classList.add('is-visible');
      else enabledElements.push(element);
    });

    if (this.reduceMotion || !('IntersectionObserver' in window)) {
      enabledElements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    if (!enabledElements.length) return;

    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver(this.onReveal, {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12,
      });
    }

    enabledElements.forEach((element) => {
      const bounds = element.getBoundingClientRect();
      const inInitialViewport =
        bounds.top < window.innerHeight * 0.94 &&
        bounds.bottom > 0 &&
        bounds.left < window.innerWidth &&
        bounds.right > 0;
      if (inInitialViewport) {
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() =>
            element.classList.add('is-visible'),
          ),
        );
      } else {
        this.revealObserver.observe(element);
      }
    });
  }

  onReveal(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      this.revealObserver.unobserve(entry.target);
    });
  }

  prepareParallax(root) {
    if (this.reduceMotion || !('IntersectionObserver' in window)) return;
    const parallaxEnabled =
      document.body?.classList.contains('parallax-enabled');
    const items = [
      ...root.querySelectorAll('[data-parallax]:not([data-parallax-ready])'),
    ].filter(
      (item) =>
        !item.closest('[data-section-motion="none"]') &&
        (parallaxEnabled || item.hasAttribute('data-story-scale')),
    );
    if (!items.length) return;

    if (!this.parallaxObserver) {
      this.parallaxObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.parallaxItems.add(entry.target);
              entry.target.classList.add('is-parallax-active');
            } else {
              this.parallaxItems.delete(entry.target);
              entry.target.classList.remove('is-parallax-active');
            }
          });
          this.onParallaxScroll();
        },
        { rootMargin: '15% 0px' },
      );
      window.addEventListener('scroll', this.onParallaxScroll, {
        passive: true,
      });
      window.addEventListener('resize', this.onParallaxScroll, {
        passive: true,
      });
    }

    items.forEach((item) => {
      item.dataset.parallaxReady = 'true';
      this.parallaxObserver.observe(item);
    });
  }

  onParallaxScroll() {
    if (this.parallaxFrame) return;
    this.parallaxFrame = window.requestAnimationFrame(() => {
      const viewportCenter = window.innerHeight / 2;
      this.parallaxItems.forEach((item) => {
        if (!item.isConnected) {
          this.parallaxItems.delete(item);
          return;
        }
        const bounds = item.getBoundingClientRect();
        const progress = Math.max(
          -1,
          Math.min(
            1,
            (bounds.top + bounds.height / 2 - viewportCenter) /
              window.innerHeight,
          ),
        );
        item.style.setProperty(
          '--parallax-y',
          `${(progress * -3).toFixed(2)}%`,
        );
        if (item.hasAttribute('data-story-scale')) {
          const edgeScale = 1.35;
          const centerScale = 1.05;
          // Only animate while the image is below center; hold at center after it passes.
          const belowCenterProgress = Math.max(0, progress);
          const scale =
            centerScale + (edgeScale - centerScale) * belowCenterProgress;
          item.style.setProperty('--media-reveal-scale', scale.toFixed(3));
        }
      });
      this.parallaxFrame = 0;
    });
  }
}

const veloraMotion = new VeloraMotion();
veloraMotion.start();
document.addEventListener('shopify:section:load', (event) =>
  veloraMotion.start(event.target),
);
document.addEventListener('velora:content:loaded', (event) =>
  veloraMotion.start(event.target),
);
